import { baseApi } from './baseApi';

export interface SignupPlan {
  key: string;
  name: string;
  tier: string;
  priceUsd: number | null;
  popular: boolean;
  limits: { users: number | null; gustavoMonthlyCredits: number | null; properties: number | null };
  features: string[];
  insights: string;
  website: string;
}

/**
 * Un país en el que se puede dar de alta una inmobiliaria. Sale del catálogo del backend
 * (`meli_sites`, sembrado desde MercadoLibre): son los 18 países con site de ML y árbol
 * de ubicaciones. Los decimales importan: CLP, COP, HNL, NIO y PYG van en cero.
 */
export interface SignupCountry {
  countryIso: string;
  countryName: string;
  siteId: string;
  currencyId: string;
  currencySymbol?: string;
  currencyDecimals: number;
  /** Centro del país (ML). Permite encuadrar un mapa en CUALQUIER país, no solo el del tenant. */
  centro?: { lat: number; lng: number };
}

export interface StartSignupBody {
  companyName: string;
  /** ISO-2 del país elegido. Obligatorio: sin país el backend rechaza el alta. */
  country: string;
  planKey: string;
  adminName: string;
  adminLastName?: string;
  adminEmail: string;
  adminPhone?: string;
  password: string;
  domainAddon?: boolean;
  successUrl?: string;
  backUrl?: string;
}

const signupApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listSignupPlans: build.query<SignupPlan[], void>({
      query: () => '/signup/plans',
      transformResponse: (r: { data: SignupPlan[] }) => r.data,
    }),
    listSignupCountries: build.query<SignupCountry[], void>({
      query: () => '/signup/countries',
      transformResponse: (r: { data: SignupCountry[] }) => r.data,
      keepUnusedDataFor: 3600, // cambia una vez cada muchos meses
    }),
    startSignup: build.mutation<{ signupId: string; paymentId: string; merchantCheckoutToken: string }, StartSignupBody>({
      query: (body) => ({ url: '/signup/start', method: 'POST', body }),
      transformResponse: (r: { data: { signupId: string; paymentId: string; merchantCheckoutToken: string } }) => r.data,
    }),
    // Alta gratis (Colaborador): sin tarjeta, provisiona en el acto.
    startFreeSignup: build.mutation<{ signupId: string; status: string; dashboardUrl: string }, Omit<StartSignupBody, 'planKey' | 'successUrl' | 'backUrl'>>({
      query: (body) => ({ url: '/signup/start-free', method: 'POST', body }),
      transformResponse: (r: { data: { signupId: string; status: string; dashboardUrl: string } }) => r.data,
    }),
    confirmSignup: build.mutation<{ status: string; redirectUrl?: string }, { paymentId: string; cardToken: string; document: string }>({
      query: (body) => ({ url: '/signup/confirm', method: 'POST', body }),
      transformResponse: (r: { data: { status: string; redirectUrl?: string } }) => r.data,
    }),
    getSignupStatus: build.query<{ status: string; dashboardUrl: string | null; error: string | null }, string>({
      query: (id) => `/signup/status/${id}`,
      transformResponse: (r: { data: { status: string; dashboardUrl: string | null; error: string | null } }) => r.data,
    }),
    // Cupón de días gratis: validar + registrarse aplicándolo (provisiona gratis con plan+trial del cupón).
    validateCoupon: build.query<{ code: string; planKey: string; planName: string; freeDays: number }, string>({
      query: (code) => `/signup/validate-coupon?code=${encodeURIComponent(code)}`,
      transformResponse: (r: { data: { code: string; planKey: string; planName: string; freeDays: number } }) => r.data,
    }),
    startCouponSignup: build.mutation<
      { signupId: string; status: string; dashboardUrl: string; plan: string; freeDays: number },
      { companyName: string; country: string; adminName: string; adminLastName?: string; adminEmail: string; adminPhone?: string; password: string; couponCode: string }
    >({
      query: (body) => ({ url: '/signup/start-coupon', method: 'POST', body }),
      transformResponse: (r: { data: { signupId: string; status: string; dashboardUrl: string; plan: string; freeDays: number } }) => r.data,
    }),
  }),
});

export const {
  useListSignupPlansQuery,
  useListSignupCountriesQuery,
  useStartSignupMutation,
  useStartFreeSignupMutation,
  useConfirmSignupMutation,
  useLazyGetSignupStatusQuery,
  useLazyValidateCouponQuery,
  useStartCouponSignupMutation,
} = signupApi;
